import { useNavigate, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useSecurity } from "../context/SecurityContext";
import { api } from "../lib/api";
import AdminImpersonateBar from "./AdminImpersonateBar";
import AccountStatusBadge from "./AccountStatusBadge";
import { FeedbackButton } from "./FeedbackButton";
import { InstallPrompt } from "./InstallPrompt";
import { NotificationPrompt } from "./NotificationPrompt";
import { isStandalone, isMobileDevice } from "../lib/installPrompt";
import {
  LogOut, Sparkles, BarChart3, Users, ClipboardList, Lock,
  Settings as SettingsIcon, Shield, CreditCard, Network, Menu, X, Download, Smartphone,
} from "lucide-react";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { lockNow } = useSecurity();
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [feedbackNew, setFeedbackNew] = useState(0);

  // Admin-only "new feedback" badge — polls every 60s + refreshes on demand
  useEffect(() => {
    if (!user?.is_admin) return undefined;
    const fetchBadge = () => api.get("/feedback/badge").then((r) => setFeedbackNew(r.data.new_count)).catch(() => {});
    fetchBadge();
    const iv = setInterval(fetchBadge, 60000);
    window.addEventListener("pstr-feedback-badge", fetchBadge);
    return () => { clearInterval(iv); window.removeEventListener("pstr-feedback-badge", fetchBadge); };
  }, [user]);

  // Close drawer on route change
  useEffect(() => {
    let cancelled = false;
    Promise.resolve().then(() => { if (!cancelled) setMobileOpen(false); });
    return () => { cancelled = true; };
  }, [path]);

  const isInsights = path === "/insights";
  const isProtocols = path === "/protocols";
  const isCommunity = path.startsWith("/community");
  const isSettings = path === "/settings";
  const isBilling = path.startsWith("/billing");
  const isAdmin = path.startsWith("/admin");
  const isClients = !isInsights && !isProtocols && !isCommunity && !isSettings && !isAdmin && !isBilling;

  const links = [
    { to: "/", active: isClients, icon: <Users className="w-4 h-4" strokeWidth={1.5} />, label: "Clients", testid: "nav-clients" },
    { to: "/protocols", active: isProtocols, icon: <ClipboardList className="w-4 h-4" strokeWidth={1.5} />, label: "Protocols", testid: "nav-protocols" },
    { to: "/community", active: isCommunity, icon: <Network className="w-4 h-4" strokeWidth={1.5} />, label: "Community", testid: "nav-community" },
    { to: "/insights", active: isInsights, icon: <BarChart3 className="w-4 h-4" strokeWidth={1.5} />, label: "Insights", testid: "nav-insights" },
    { to: "/billing", active: isBilling, icon: <CreditCard className="w-4 h-4" strokeWidth={1.5} />, label: "Billing", testid: "nav-billing" },
    { to: "/settings", active: isSettings, icon: <SettingsIcon className="w-4 h-4" strokeWidth={1.5} />, label: "Settings", testid: "nav-settings" },
    ...(user?.is_admin ? [{ to: "/admin/usage", active: isAdmin, icon: <Shield className="w-4 h-4" strokeWidth={1.5} />, label: "Admin", testid: "nav-admin", badge: feedbackNew }] : []),
  ];

  return (
    <div className="min-h-screen bg-paper-50 flex flex-col overflow-x-hidden">
      <header className="border-b border-paper-200 bg-paper-50/80 backdrop-blur-sm sticky top-0 z-40 print:hidden">
        <div className="max-w-7xl mx-auto pl-4 sm:pl-6 lg:pl-8 pr-2 sm:pr-6 lg:pr-8 h-16 flex items-center gap-2">
          <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="header-logo">
            <div className="w-9 h-9 rounded-xl bg-sage-500 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <div className="leading-tight hidden sm:block">
              <div className="font-heading text-lg font-medium tracking-tight text-ink-900">Posturra</div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-ink-500">Massage Therapy Studio</div>
            </div>
            <div className="font-heading text-lg font-medium tracking-tight text-ink-900 sm:hidden">Posturra</div>
          </Link>

          {/* Desktop nav (md+) */}
          <nav className="hidden md:flex flex-1 items-center gap-1 justify-end">
            {links.map((l) => <NavLink key={l.testid} {...l} />)}
            <AccountStatusBadge />
            <button
              data-testid="lock-app-btn"
              onClick={lockNow}
              title="Lock screen — hide all client information"
              className="ml-1 min-h-[44px] w-11 rounded-full border border-paper-200 hover:bg-paper-100 transition-all inline-flex items-center justify-center text-ink-700 shrink-0"
            >
              <Lock className="w-4 h-4" strokeWidth={1.5} />
            </button>
            <button
              data-testid="logout-btn"
              onClick={() => { logout(); navigate("/login"); }}
              className="ml-1 sm:ml-2 min-h-[44px] px-3 sm:px-4 rounded-full border border-paper-200 hover:bg-paper-100 transition-all inline-flex items-center gap-2 text-sm text-ink-700 shrink-0"
              title={user?.email}
            >
              <LogOut className="w-4 h-4" strokeWidth={1.5} />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </nav>

          {/* Mobile menu button (below md) */}
          <div className="md:hidden ml-auto flex items-center gap-2">
            <AccountStatusBadge />
            <button
              type="button"
              data-testid="lock-app-btn-mobile"
              onClick={lockNow}
              aria-label="Lock screen"
              className="w-11 h-11 rounded-full border border-paper-200 inline-flex items-center justify-center text-ink-700 hover:bg-paper-100"
            >
              <Lock className="w-4 h-4" strokeWidth={1.5} />
            </button>
            <button
              type="button"
              data-testid="mobile-nav-toggle"
              onClick={() => setMobileOpen(true)}
              aria-label="Open navigation menu"
              className="w-11 h-11 rounded-full border border-paper-200 inline-flex items-center justify-center text-ink-700 hover:bg-paper-100"
            >
              <Menu className="w-5 h-5" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="mobile-nav-drawer">
          <div
            className="absolute inset-0 bg-ink-900/40 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute top-0 right-0 bottom-0 w-[82%] max-w-sm bg-paper-50 shadow-2xl flex flex-col">
            <div className="h-16 px-4 flex items-center justify-between border-b border-paper-200 shrink-0">
              <span className="font-heading text-lg text-ink-900">Menu</span>
              <button
                type="button"
                data-testid="mobile-nav-close"
                onClick={() => setMobileOpen(false)}
                aria-label="Close navigation menu"
                className="w-11 h-11 rounded-full hover:bg-paper-100 inline-flex items-center justify-center text-ink-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
              {links.map((l) => (
                <Link
                  key={l.testid}
                  to={l.to}
                  data-testid={`${l.testid}-mobile`}
                  className={`flex items-center gap-3 min-h-[48px] px-4 rounded-2xl text-base ${l.active ? "bg-ink-900 text-white" : "text-ink-700 hover:bg-paper-100"}`}
                >
                  {l.icon}<span>{l.label}</span>
                  {l.badge > 0 && (
                    <span data-testid={`${l.testid}-badge-mobile`} className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-semibold flex items-center justify-center">
                      {l.badge > 99 ? "99+" : l.badge}
                    </span>
                  )}
                </Link>
              ))}
              {isClients && (
                <div className="pt-3 mt-3 border-t border-paper-200">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-ink-500 px-4 mb-1">Administrative</div>
                  <button
                    type="button"
                    data-testid="export-csv-mobile"
                    onClick={() => {
                      setMobileOpen(false);
                      window.dispatchEvent(new Event("pstr-open-csv-export"));
                    }}
                    className="w-full flex items-center gap-3 min-h-[48px] px-4 rounded-2xl text-base text-ink-700 hover:bg-paper-100"
                  >
                    <Download className="w-4 h-4" strokeWidth={1.5} />
                    <span>Export CSV</span>
                  </button>
                </div>
              )}
              {!isStandalone() && isMobileDevice() && (
                <button
                  type="button"
                  data-testid="install-menu-item"
                  onClick={() => {
                    setMobileOpen(false);
                    window.dispatchEvent(new Event("pstr-open-install"));
                  }}
                  className="w-full flex items-center gap-3 min-h-[48px] px-4 rounded-2xl text-base text-ink-700 hover:bg-paper-100"
                >
                  <Smartphone className="w-4 h-4" strokeWidth={1.5} />
                  <span>Add to Home Screen</span>
                </button>
              )}
            </nav>
            <div className="border-t border-paper-200 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
              <div className="text-xs text-ink-500 px-2 mb-2 truncate">{user?.email}</div>
              <button
                data-testid="logout-btn-mobile"
                onClick={() => { logout(); navigate("/login"); }}
                className="w-full min-h-[48px] px-4 rounded-2xl border border-paper-200 hover:bg-paper-100 inline-flex items-center justify-center gap-2 text-sm text-ink-700"
              >
                <LogOut className="w-4 h-4" strokeWidth={1.5} /> Sign out
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 md:py-12 flex-1 w-full">
        {children}
      </main>
      <footer className="border-t border-paper-200 bg-paper-50 mt-12 print:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap items-center justify-between gap-3 text-xs text-ink-500">
          <span>© Posturra · For educational &amp; documentation support only — not a medical device.</span>
          <div className="flex items-center gap-4">
            <span className="text-ink-400" data-testid="app-version">v1.0.0-rc.1</span>
            <Link to="/disclaimer" className="hover:text-ink-900 underline-offset-4 hover:underline" data-testid="footer-disclaimer-link">
              Full disclaimer
            </Link>
          </div>
        </div>
      </footer>
      <AdminImpersonateBar />
      <FeedbackButton />
      <InstallPrompt />
      <NotificationPrompt />
    </div>
  );
}

function NavLink({ to, active, icon, label, testid, badge }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`relative min-h-[44px] px-3 sm:px-4 rounded-full text-sm inline-flex items-center gap-2 transition-all shrink-0 ${active ? "bg-ink-900 text-white" : "text-ink-700 hover:bg-paper-100"}`}
    >
      {icon}<span>{label}</span>
      {badge > 0 && (
        <span data-testid={`${testid}-badge`} className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center">
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </Link>
  );
}
